import { useContext, useRef, useEffect } from "react"

import { graphql, PageProps } from "gatsby"
import { useI18next } from "gatsby-plugin-react-i18next"
import styled from "styled-components"

import Layout from "components/Layout"
import { Lightmode, PageContentContext } from "components/Providers"
import SEO from "components/Seo"
import Hero from "sections/home/1-Hero"
import Bottom from "sections/home/10-BottomCallout"
import Logos from "sections/home/2-Logos"
import Platform from "sections/home/3-Platform"
import Reinventing from "sections/home/4-Reinventing"
import SinglePoint from "sections/home/5-SinglePoint"
import Glance from "sections/home/6-AtAGlance"
import People from "sections/home/7-PeopleAchieve"
import Carousel from "sections/home/8-HomeCarousel"
import Secured from "sections/home/9-SecuredCredit"
import colors from "styles/colors"
import media from "styles/media"
import { QueryHomePage } from "types/gatsby-alias"
import useMedia from "utils/useMedia"
import { translateSeo } from "utils/functions"

interface Props extends PageProps {
  data: QueryHomePage
  pageContext: { language: string }
}

export default function IndexPage({
  data,
  pageContext: { language },
  location,
}: Props) {
  const bgColorRef = useRef<HTMLDivElement>(null)
  const { setLightmode } = useContext(Lightmode)
  const { activePageContent, setActivePage } = useContext(PageContentContext)
  const hideOverflow = useMedia(false, false, false, true)

  useEffect(() => {
    setLightmode(false)
    setActivePage("home")
  }, [setLightmode, setActivePage])
  const {
    logo1,
    logo2,
    logo3,
    logo4,
    logo6,
    logo7,
    logo8,
    logo9,
    logo10,
    logo12,
    cardsImg,
    core,
    digital,
    payments,
    risk,
    security,
    cardsImgM,
    coreM,
    digitalM,
    paymentsM,
    riskM,
    securityM,
    goodGrocer,
    iPhone,
    ourPeople,
    mainCard,
  } = data

  const allLogos: { one: string; two: string }[] = [
    { one: logo1?.publicURL ?? "none", two: logo6?.publicURL ?? "none" },
    { one: logo2?.publicURL ?? "none", two: logo7?.publicURL ?? "none" },
    { one: logo3?.publicURL ?? "none", two: logo8?.publicURL ?? "none" },
    { one: logo4?.publicURL ?? "none", two: logo9?.publicURL ?? "none" },
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    { one: logo12?.publicURL ?? "none", two: logo10?.publicURL ?? "none" },
  ]

  const allCarouselImages = {
    coreVal: core?.childrenImageSharp?.[0]?.gatsbyImageData,
    coreMVal: coreM?.childrenImageSharp?.[0]?.gatsbyImageData,
    digitalVal: digital?.childrenImageSharp?.[0]?.gatsbyImageData,
    digitalMVal: digitalM?.childrenImageSharp?.[0]?.gatsbyImageData,
    cardVal: cardsImg?.childrenImageSharp?.[0]?.gatsbyImageData,
    cardsMVal: cardsImgM?.childrenImageSharp?.[0]?.gatsbyImageData,
    riskVal: risk?.childrenImageSharp?.[0]?.gatsbyImageData,
    riskMVal: riskM?.childrenImageSharp?.[0]?.gatsbyImageData,
    paymentsVal: payments?.childrenImageSharp?.[0]?.gatsbyImageData,
    paymentsValM: paymentsM?.childrenImageSharp?.[0]?.gatsbyImageData,
    securityVal: security?.childrenImageSharp?.[0]?.gatsbyImageData,
    securityMVal: securityM?.childrenImageSharp?.[0]?.gatsbyImageData,
  }

  const allAnimationImages = {
    goodGrocer: goodGrocer?.childrenImageSharp?.[0]?.gatsbyImageData,
    iPhone: iPhone?.childrenImageSharp?.[0]?.gatsbyImageData,
    mainCard: mainCard?.childrenImageSharp?.[0]?.gatsbyImageData,
    ourPeople: ourPeople?.childrenImageSharp?.[0]?.gatsbyImageData,
  }

  const localePageData = activePageContent?.find(pageContent => {
    if (pageContent.node_locale === language) {
      return true
    }
    return false
  })

  return (
    <Layout pathname={location.pathname} id="home" hideOverflow={hideOverflow}>
      <Hero />
      <Logos allLogos={allLogos} />
      <Platform />

      <LowerBackground>
        {/* <Reinventing allAnimationImages={allAnimationImages} /> */}
        <ChangingBackground ref={bgColorRef}>
          <SinglePoint bgRef={bgColorRef} />
          <Glance />
        </ChangingBackground>
      </LowerBackground>

      <People allAnimationImages={allAnimationImages} />
      <Carousel allCarouselImages={allCarouselImages} />
      {localePageData && <Secured activePageContent={localePageData} />}
      <Bottom />
    </Layout>
  )
}

export function Head({data, location, }: Props) {
  const { t } = useI18next()
  const title = "Galileo Financial Technology Platform | Banking, Fintech, APIs"
  const description =
    "Galileo is the financial technology platform connecting people with money and shaping fintech by offering core banking, card issuing, and payment processing and more."
  const { translatedTitle, translatedDesc } = translateSeo(
    data.locales.edges[0]?.node.data ?? null,
    title,
    description
  )
  const attributeData = {
    title:translatedTitle,
    description: translatedDesc,
    url: location.pathname,
    originalPath: location.pathname,
    id: "home",
  }
  return <SEO pageAttributes={attributeData} />
}

/**
 * I'm using two backgrounds because the changing one needs to overlap
 * the reinventing section, but the lower background cannot
 */
const LowerBackground = styled.div`
  background-color: ${colors.darkMode};
`

const ChangingBackground = styled.div`
  margin-top: -100vh;
  padding-top: 100vh;
  width: 100%;
  background-color: transparent;

  position: relative;
  z-index: 1;
  ${media.mobile} {
    /* overflow: hidden; */
  }

  /* add a shadow to prevent a harsh line between sections on unusual screen sizes */
  ::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100px;
    // background: linear-gradient(
    //   to bottom,
    //   rgba(0, 0, 0, 0) 0%,
    //   ${colors.lightMode} 100%
    // );
  }
`

export const query = graphql`
  query HomePage($language: String!) {
    locales: allLocale(
      filter: { ns: { in: ["index"] }, language: { eq: $language } }
    ) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
    logo1: file(name: { eq: "logo1" }, sourceInstanceName: { eq: $language }) {
      name
      publicURL
      id
    }
    logo2: file(name: { eq: "logo2" }, sourceInstanceName: { eq: $language }) {
      name
      publicURL
      id
    }
    logo3: file(name: { eq: "logo3" }, sourceInstanceName: { eq: $language }) {
      name
      publicURL
      id
    }
    logo4: file(name: { eq: "logo4" }, sourceInstanceName: { eq: $language }) {
      name
      publicURL
      id
    }
    logo5: file(name: { eq: "logo5" }, sourceInstanceName: { eq: $language }) {
      name
      publicURL
      id
    }
    logo6: file(name: { eq: "logo6" }, sourceInstanceName: { eq: $language }) {
      name
      publicURL
      id
    }
    logo7: file(name: { eq: "logo7" }, sourceInstanceName: { eq: $language }) {
      name
      publicURL
      id
    }
    logo8: file(name: { eq: "logo8" }, sourceInstanceName: { eq: $language }) {
      name
      publicURL
      id
    }
    logo9: file(name: { eq: "logo9" }, sourceInstanceName: { eq: $language }) {
      name
      publicURL
      id
    }
    logo10: file(
      name: { eq: "logo10" }
      sourceInstanceName: { eq: $language }
    ) {
      name
      publicURL
      id
    }
    logo12: file(
      name: { eq: "logo12" }
      sourceInstanceName: { eq: $language }
    ) {
      name
      publicURL
      id
    }
    cardsImg: file(
      name: { eq: "Cardsimg" }
      sourceInstanceName: { eq: $language }
    ) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: BLURRED)
      }
    }
    loan: file(name: { eq: "Loan" }, sourceInstanceName: { eq: $language }) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: BLURRED)
      }
    }
    insights: file(
      name: { eq: "Insights" }
      sourceInstanceName: { eq: $language }
    ) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: BLURRED)
      }
    }
    reader: file(
      name: { eq: "Reader" }
      sourceInstanceName: { eq: $language }
    ) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: BLURRED)
      }
    }
    core: file(name: { eq: "Core" }, sourceInstanceName: { eq: $language }) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: PNG, placeholder: BLURRED)
      }
    }
    digital: file(name: { eq: "Digital" }, sourceInstanceName: { eq: $language }) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: PNG, placeholder: BLURRED)
      }
    }
    digitalM: file(name: { eq: "DigitalM" }, sourceInstanceName: { eq: $language }) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: PNG, placeholder: BLURRED)
      }
    }
    security: file(name: { eq: "Security" }, sourceInstanceName: { eq: $language }) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: BLURRED)
      }
    }
    payments: file(name: { eq: "Payments" }, sourceInstanceName: { eq: $language }) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: BLURRED)
      }
    }
    risk: file(name: { eq: "Risk" }, sourceInstanceName: { eq: $language }) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: PNG, placeholder: BLURRED)
      }
    }
    deposits: file(
      name: { eq: "Deposits" }
      sourceInstanceName: { eq: $language }
    ) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: BLURRED)
      }
    }
    cardsImgM: file(
      name: { eq: "CardsimgM" }
      sourceInstanceName: { eq: $language }
    ) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: BLURRED)
      }
    }
    loanM: file(name: { eq: "LoanM" }, sourceInstanceName: { eq: $language }) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: BLURRED)
      }
    }
    securityM: file(name: { eq: "SecurityM" }, sourceInstanceName: { eq: $language }) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: BLURRED)
      }
    }
    paymentsM: file(name: { eq: "PaymentsM" }, sourceInstanceName: { eq: $language }) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: BLURRED)
      }
    }
    insightsM: file(
      name: { eq: "InsightsM" }
      sourceInstanceName: { eq: $language }
    ) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: BLURRED)
      }
    }
    coreM: file(name: { eq: "Core" }, sourceInstanceName: { eq: $language }) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: PNG, placeholder: BLURRED)
      }
    }
    readerM: file(
      name: { eq: "ReaderM" }
      sourceInstanceName: { eq: $language }
    ) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: BLURRED)
      }
    }
    riskM: file(name: { eq: "RiskM" }, sourceInstanceName: { eq: $language }) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: BLURRED)
      }
    }
    depositsM: file(
      name: { eq: "DepositsM" }
      sourceInstanceName: { eq: $language }
    ) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: BLURRED)
      }
    }
    goodGrocer: file(
      name: { eq: "Good_Grocer" }
      sourceInstanceName: { eq: $language }
    ) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: NONE)
      }
    }
    iPhone: file(
      name: { eq: "Iphone" }
      sourceInstanceName: { eq: $language }
    ) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: NONE)
      }
    }
    ourPeople: file(
      name: { eq: "ourPeoples" }
      sourceInstanceName: { eq: $language }
    ) {
      name
      childrenImageSharp {
        gatsbyImageData(
          width: 905
          height: 519
          formats: PNG
          placeholder: NONE
        )
      }
    }
    mainCard: file(
      name: { eq: "Card_Illustration" }
      sourceInstanceName: { eq: $language }
    ) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: NONE)
      }
    }
  }
`
